import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck, Download, FileText, Users, Truck, DollarSign, Settings as SettingsIcon,
  BadgeCheck, TrendingUp, Receipt, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { FreeToolTab } from "@/components/AdminFreeTool";
import { DirectoryTab } from "@/components/AdminDirectory";
import { BrandKitTab } from "@/components/AdminBrandKit";
import { FilterSelect, AU_STATES, CONTACT_OPTIONS } from "@/components/AdminFilters";

const JOIN_OPTIONS = [["email", "Signed up with email"], ["google", "Signed up with Google"], ["directory_claim", "Claimed a directory listing"]];
const remHas = (r, k) => Boolean({ email: r.email, abn: r.abn_or_license, phone: r.phone, website: r.website }[k]);
const remMatch = (r, f, skip) => (skip === "state" || !f.state || r.states?.includes(f.state))
  && (skip === "approval" || !f.approval || r.approval_status === f.approval)
  && (skip === "contact" || !f.contact || (f.contact.startsWith("no_") ? !remHas(r, f.contact.slice(3)) : remHas(r, f.contact)))
  && (skip === "source" || !f.source || r.source === f.source);

function RemovalistFilters({ rems, f, set, shown }) {
  const count = (key, pred) => rems.filter((r) => remMatch(r, f, key) && pred(r)).length;
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="rem-category-filters">
      <FilterSelect label="State" value={f.state} onChange={(v) => set("state", v)} testid="rem-state-filter" allLabel="All states" allCount={count("state", () => true)}
        options={AU_STATES.map((s) => [s, s, count("state", (r) => r.states?.includes(s))])} />
      <FilterSelect label="Approved" value={f.approval} onChange={(v) => set("approval", v)} testid="rem-approval-filter" allCount={count("approval", () => true)}
        options={[["approved", "Approved"], ["pending", "Pending"], ["rejected", "Rejected"]].map(([k, l]) => [k, l, count("approval", (r) => r.approval_status === k)])} />
      <FilterSelect label="Contact details" value={f.contact} onChange={(v) => set("contact", v)} testid="rem-contact-filter" allLabel="Any" allCount={count("contact", () => true)}
        options={CONTACT_OPTIONS.filter(([k]) => k !== "no_email").map(([k, l]) => [k, l, count("contact", (r) => (k.startsWith("no_") ? !remHas(r, k.slice(3)) : remHas(r, k)))])} />
      <FilterSelect label="How they joined" value={f.source} onChange={(v) => set("source", v)} testid="rem-source-filter" allLabel="Any" allCount={count("source", () => true)}
        options={JOIN_OPTIONS.map(([k, l]) => [k, l, count("source", (r) => r.source === k)])} />
      <p className="text-sm text-muted-foreground pb-2" data-testid="rem-showing-count">Showing <strong className="text-foreground">{shown}</strong> of {rems.length}</p>
    </div>
  );
}

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Stat({ icon: Icon, label, value, accent, sub }) {
  return (
    <Card data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-card">
      <CardContent className="p-3 sm:p-5 flex items-center gap-2.5 sm:gap-4">
        <div className={`h-9 w-9 sm:h-11 sm:w-11 shrink-0 rounded-lg flex items-center justify-center ${accent || "bg-asphalt"}`}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-lg sm:text-2xl font-display font-extrabold leading-none break-words">{value}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mt-1">{label}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`admin-stat-sub-${label.toLowerCase().replace(/\s+/g, "-")}`}>{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

async function downloadPdf(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
    toast.success("Statement downloaded");
  } catch (e) {
    toast.error("Could not generate PDF");
  }
}

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [month, setMonth] = useState(thisMonth());

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-kinetic flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight" data-testid="admin-title">
              Admin Console
            </h1>
            <p className="text-sm text-muted-foreground">Platform billing, verification & performance</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Stat icon={Users} label="Customers" value={overview?.customers ?? "—"} />
          <Stat icon={Truck} label="Removalists" value={overview?.removalists ?? "—"} accent="bg-slate-600" />
          <Stat icon={Receipt} label="Accepted Bids" value={overview?.accepted_bids ?? "—"} accent="bg-emerald-600" />
          <Stat icon={DollarSign} label="Total Fees" value={overview ? money(overview.total_fees) : "—"} accent="bg-kinetic" />
          <Stat icon={Sparkles} label="AI Spend" value={overview ? `US$${overview.ai_spend_usd.toFixed(2)}` : "—"} accent="bg-sky-700"
            sub={overview ? `US$${overview.ai_spend_month_usd.toFixed(2)} this month · ${overview.ai_videos} videos · free tool US$${overview.guest_ai_spend_usd.toFixed(2)}` : null} />
        </div>

        <Tabs defaultValue="billing" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
            <TabsTrigger value="statements" data-testid="tab-statements">Customer Statements</TabsTrigger>
            <TabsTrigger value="summaries" data-testid="tab-summaries">Client Summaries</TabsTrigger>
            <TabsTrigger value="verification" data-testid="tab-verification">Approvals</TabsTrigger>
            <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
            <TabsTrigger value="free-tool" data-testid="tab-free-tool">Free Inventory Tool</TabsTrigger>
            <TabsTrigger value="brand" data-testid="tab-brand">Brand Kit</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="billing">
            <BillingTab month={month} setMonth={setMonth} />
          </TabsContent>
          <TabsContent value="statements">
            <StatementsTab month={month} setMonth={setMonth} />
          </TabsContent>
          <TabsContent value="summaries">
            <SummariesTab month={month} setMonth={setMonth} />
          </TabsContent>
          <TabsContent value="verification">
            <VerificationTab onChange={loadOverview} />
          </TabsContent>
          <TabsContent value="directory">
            <DirectoryTab />
          </TabsContent>
          <TabsContent value="free-tool">
            <FreeToolTab />
          </TabsContent>
          <TabsContent value="brand">
            <BrandKitTab />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsTab onSaved={loadOverview} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function MonthPicker({ month, setMonth }) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="month" className="text-xs uppercase tracking-wider text-muted-foreground">Period</Label>
      <Input id="month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
        data-testid="month-picker" className="w-44" />
    </div>
  );
}

function BillingTab({ month, setMonth }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/accepted-bids?month=${month}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [month]);
  return (
    <Card className="bg-card">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-lg">Accepted Bids — Platform Fees</CardTitle>
        <MonthPicker month={month} setMonth={setMonth} />
      </CardHeader>
      <CardContent>
        <div className="flex gap-6 mb-4 text-sm">
          <span>Accepted: <b data-testid="billing-count">{data?.items?.length ?? 0}</b></span>
          <span>Bid value: <b>{money(data?.total_amount)}</b></span>
          <span className="text-kinetic">Fees due: <b data-testid="billing-total-fees">{money(data?.total_fees)}</b></span>
          <span className="text-muted-foreground">Fee/bid: {money(data?.platform_fee)}</span>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="billing-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Job</TableHead><TableHead>Customer</TableHead>
                <TableHead>Removalist</TableHead><TableHead className="text-right">Bid</TableHead>
                <TableHead className="text-right">Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.items || []).map((r) => (
                <TableRow key={r.bid_id} data-testid={`billing-row-${r.bid_id}`}>
                  <TableCell className="whitespace-nowrap">{(r.accepted_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-medium">{r.job_title}</TableCell>
                  <TableCell className="text-muted-foreground">{r.customer_name}</TableCell>
                  <TableCell>{r.removalist_name}</TableCell>
                  <TableCell className="text-right">{money(r.amount)}</TableCell>
                  <TableCell className="text-right text-kinetic font-semibold">{money(r.fee)}</TableCell>
                </TableRow>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No accepted bids in this period
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function StatementsTab({ month, setMonth }) {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  return (
    <Card className="bg-card">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-lg">Generate Monthly Customer Statement (PDF)</CardTitle>
        <MonthPicker month={month} setMonth={setMonth} />
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table data-testid="customers-table">
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead><TableHead>Email</TableHead>
                <TableHead className="text-right">Accepted Jobs</TableHead><TableHead className="text-right">Statement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">{c.email}</TableCell>
                  <TableCell className="text-right">{c.accepted_jobs}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="gap-2"
                      data-testid={`statement-btn-${c.id}`}
                      onClick={() => downloadPdf(
                        `/admin/statements/customer/${c.id}/pdf?month=${month}`,
                        `MoveTender-Statement-${c.name}-${month}.pdf`)}>
                      <Download className="h-4 w-4" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {customers.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No customers yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function SummariesTab({ month, setMonth }) {
  const [rems, setRems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/admin/removalists").then((r) => setRems(r.data)).catch(() => {}); }, []);

  const view = async (rem) => {
    setSelected(rem);
    try {
      const { data } = await api.get(`/admin/summary/removalist/${rem.id}?month=${month}`);
      setSummary(data);
    } catch { setSummary(null); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selected) view(selected); }, [month]);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="bg-card">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-lg">Clients (Removalists)</CardTitle>
          <MonthPicker month={month} setMonth={setMonth} />
        </CardHeader>
        <CardContent className="space-y-2">
          {rems.map((r) => (
            <button key={r.id} data-testid={`summary-select-${r.id}`} onClick={() => view(r)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.id === r.id ? "border-kinetic bg-kinetic/5" : "border-border hover:bg-muted"}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{r.company_name || r.name}</span>
                {r.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
              </div>
              <span className="text-xs text-muted-foreground">{r.won} won · {r.bids} bids</span>
            </button>
          ))}
          {rems.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No removalists yet</p>}
        </CardContent>
      </Card>

      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-kinetic" /> Monthly Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {!summary ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Select a client to view their summary</p>
          ) : (
            <div className="space-y-3" data-testid="summary-panel">
              <p className="font-semibold">{summary.removalist.company_name || summary.removalist.name}</p>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Metric label="Jobs viewed" value={summary.jobs_viewed} />
                <Metric label="Jobs bid on" value={summary.jobs_bidded} />
                <Metric label="Jobs won" value={summary.jobs_won} />
                <Metric label="Win rate" value={`${summary.won_pct}%`} />
                <Metric label="Avg price gap" value={money(summary.avg_price_diff)} />
                <Metric label="Won value" value={money(summary.won_value)} />
                <Metric label="Total fees" value={money(summary.total_fees)} />
                <Metric label="Acquisition cost" value={`${summary.acquisition_cost_pct}%`} />
              </dl>
              <Button className="w-full gap-2 bg-kinetic hover:bg-kinetic-hover text-white mt-2"
                data-testid="summary-pdf-btn"
                onClick={() => downloadPdf(
                  `/admin/summary/removalist/${summary.removalist.id}/pdf?month=${month}`,
                  `MoveTender-Summary-${summary.removalist.company_name || summary.removalist.name}-${month}.pdf`)}>
                <FileText className="h-4 w-4" /> Download Summary PDF
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-muted/50 border border-border">
      <dd className="text-xl font-display font-bold">{value}</dd>
      <dt className="text-xs text-muted-foreground uppercase tracking-wider">{label}</dt>
    </div>
  );
}

function VerificationTab({ onChange }) {
  const [rems, setRems] = useState([]);
  const [f, setF] = useState({ state: "", approval: "", contact: "", source: "" });
  const shownRems = rems.filter((r) => remMatch(r, f));
  const load = useCallback(() => { api.get("/admin/removalists").then((r) => setRems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (rem, verified) => {
    try {
      await api.post(`/admin/removalists/${rem.id}/verify`, { verified });
      toast.success(verified ? "Removalist approved" : rem.approval_status === "approved" ? "Approval revoked" : "Removalist rejected");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pending = rems.filter((r) => r.approval_status === "pending").length;
  const badge = {
    approved: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><BadgeCheck className="h-3 w-3" /> Approved</Badge>,
    pending: <Badge variant="outline" className="text-amber-600 border-amber-300">Pending approval</Badge>,
    rejected: <Badge variant="outline" className="text-red-600 border-red-300">Rejected</Badge>,
  };

  return (
    <Card className="bg-card">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2">Removalist Approvals {pending > 0 && <Badge className="bg-kinetic text-white" data-testid="pending-approvals-count">{pending} pending</Badge>}</CardTitle>
        <p className="text-sm text-muted-foreground">New removalists can browse jobs but can't bid or show the verified badge until you approve them.</p></CardHeader>
      <CardContent>
        <RemovalistFilters rems={rems} f={f} set={(k, v) => setF((p) => ({ ...p, [k]: v }))} shown={shownRems.length} />
        <div className="overflow-x-auto">
          <Table data-testid="verification-table">
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead><TableHead>Business details</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shownRems.map((r) => (
                <TableRow key={r.id} data-testid={`verify-row-${r.id}`}>
                  <TableCell>
                    <p className="font-medium">{r.company_name || r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.name} · {r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                    <p className="text-xs text-muted-foreground">Joined {(r.created_at || "").slice(0, 10)}</p>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground space-y-0.5">
                    <p><span className="text-foreground">ABN / licence:</span> {r.abn_or_license || "—"}</p>
                    {r.website && <p><span className="text-foreground">Website:</span> <a href={r.website} target="_blank" rel="noreferrer" className="underline hover:text-kinetic">{r.website}</a></p>}
                    {r.insurance && <p><span className="text-foreground">Insurance:</span> {r.insurance}</p>}
                    {r.coverage?.length > 0 && <p><span className="text-foreground">Areas:</span> {r.coverage.map((c) => c.suburb).join(", ")}</p>}
                  </TableCell>
                  <TableCell data-testid={`approval-status-${r.id}`}>{badge[r.approval_status] || badge.pending}</TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-2">
                    {r.approval_status !== "approved" && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-btn-${r.id}`} onClick={() => decide(r, true)}>Approve</Button>
                    )}
                    {r.approval_status !== "rejected" && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid={`reject-btn-${r.id}`} onClick={() => decide(r, false)}>
                        {r.approval_status === "approved" ? "Revoke" : "Reject"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {shownRems.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">{rems.length ? "No removalists match these filters" : "No removalists yet"}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsTab({ onSaved }) {
  const [fee, setFee] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => { setFee(String(r.data.platform_fee)); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const save = async () => {
    try {
      await api.put("/admin/settings", { platform_fee: parseFloat(fee) });
      toast.success("Platform fee updated");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card className="bg-card max-w-lg">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><SettingsIcon className="h-5 w-5" /> Platform Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="fee">Flat platform fee per accepted bid (AUD)</Label>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <Input id="fee" type="number" min="0" step="0.5" value={fee} disabled={!loaded}
              onChange={(e) => setFee(e.target.value)} data-testid="fee-input" className="w-40" />
          </div>
          <p className="text-xs text-muted-foreground">Charged to the customer for each bid they accept. Used across billing statements and summaries.</p>
        </div>
        <Button onClick={save} className="bg-kinetic hover:bg-kinetic-hover text-white gap-2" data-testid="save-fee-btn">
          <DollarSign className="h-4 w-4" /> Save Fee
        </Button>
      </CardContent>
    </Card>
  );
}
